import React, { useEffect, useState } from "react";
import { FILTER_FIELDS, FIELD_BY_KEY } from "../../inventory/fields";
import { activeFilterCount, filterOptions } from "../../inventory/filters";
import MultiSelect from "../../components/ui/MultiSelect";
import Popover from "../../components/ui/Popover";
import Icon from "../../components/ui/Icon";

const EMPTY = { q: "", lists: {}, cpmMax: "", reachMin: "" };

// `leading` goes before the search box (the saved views menu)
export default function FilterBar({ channels, filters, onChange, leading = null }) {
  // The search box keeps its own text so typing stays smooth; the URL
  // follows a moment later.
  const [q, setQ] = useState(filters.q);
  useEffect(() => setQ(filters.q), [filters.q]);
  useEffect(() => {
    if (q === filters.q) return undefined;
    // Not trimmed: the text comes back into the box, and trimming would eat
    // the space between words while someone is still typing
    const t = setTimeout(() => onChange({ ...filters, q }), 200);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const setList = (key, values) => {
    const lists = { ...filters.lists };
    if (values.length) lists[key] = values;
    else delete lists[key];
    onChange({ ...filters, lists });
  };

  // Search box included: it empties at once instead of waiting for the URL
  const reset = () => {
    setQ("");
    onChange(EMPTY);
  };

  const rangeActive = ["cpmMax", "reachMin"].filter((k) => filters[k] !== "").length;
  const total = activeFilterCount(filters);

  // Chips for everything that's on, each with its own ×
  const chips = [];
  Object.entries(filters.lists).forEach(([key, values]) =>
    values.forEach((v) =>
      chips.push({
        id: `${key}:${v}`,
        label: `${FIELD_BY_KEY[key].label}: ${v}`,
        clear: () => setList(key, values.filter((x) => x !== v)),
      })
    )
  );
  if (filters.cpmMax !== "")
    chips.push({ id: "cpmMax", label: `CPM ≤ ${filters.cpmMax}`, clear: () => onChange({ ...filters, cpmMax: "" }) });
  if (filters.reachMin !== "")
    chips.push({ id: "reachMin", label: `Reach ≥ ${filters.reachMin}`, clear: () => onChange({ ...filters, reachMin: "" }) });

  return (
    <div className="filterbar">
      <div className="filterbar-row">
        {leading}
        <div className="search-box">
          <Icon name="search" />
          <input
            type="search"
            placeholder="Search channels, genres, partners…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search inventory"
          />
        </div>

        <div className="filter-buttons">
          {FILTER_FIELDS.map((field) => (
            <MultiSelect
              key={field.key}
              label={field.label}
              options={filterOptions(channels, field)}
              selected={filters.lists[field.key] || []}
              onChange={(values) => setList(field.key, values)}
            />
          ))}

          <Popover
            triggerClass={`filter-btn${rangeActive ? " is-active" : ""}`}
            trigger={
              <>
                CPM and reach
                {rangeActive > 0 && <span className="filter-count">{rangeActive}</span>}
                <Icon name="chevron" size={14} />
              </>
            }
          >
            <div className="range-panel">
              <div className="form-field">
                <label htmlFor="cpm-max">Maximum CPM</label>
                <input
                  id="cpm-max"
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Max"
                  value={filters.cpmMax}
                  onChange={(e) => onChange({ ...filters, cpmMax: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label htmlFor="reach-min">Minimum reach</label>
                <input
                  id="reach-min"
                  className="form-input"
                  placeholder="10M"
                  value={filters.reachMin}
                  onChange={(e) => onChange({ ...filters, reachMin: e.target.value })}
                />
                <span className="form-hint">Accepts 500K, 10M, 1.2B</span>
              </div>
            </div>
          </Popover>

          <button
            type="button"
            className={`filter-reset${total ? " is-active" : ""}`}
            onClick={reset}
            disabled={!total}
            title={total ? "Clear the search and every filter" : "No filters to reset"}
          >
            <Icon name="refresh" size={14} />
            Reset
            {total > 0 && <span className="filter-count">{total}</span>}
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="filter-chips">
          {chips.map((c) => (
            <span key={c.id} className="filter-chip">
              {c.label}
              <button type="button" aria-label={`Remove ${c.label}`} onClick={c.clear}>
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export { EMPTY as EMPTY_FILTERS };
